import { PanelDisclosure } from '../shared/PanelDisclosure';
import { FieldSelect } from '../shared/FieldSelect';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert } from '@/components/ui/alert';
import { api } from '@services/api';
import { emptyInputs, mainlineApi, type MainlineCommand, type MainlineInputs, type MainlineState, type RowKind } from '@services/drbfm.mainline';
import InputsPanel from './mainline/InputsPanel';
import RowsPanel from './mainline/RowsPanel';
import ProposalPanel from './mainline/ProposalPanel';
import { rowTitle } from './mainline/fields';
import { mainlineErrorMessage } from './mainline/mainlineErrorMessage';
import type { MainlineStatus } from './mainline/useMainlineStatus';

const STATUS_NOTICE: Partial<Record<MainlineStatus, string>> = {
  checking: 'drbfm.mainline.statusChecking',
  disabled: 'drbfm.mainline.readOnly',
  unavailable: 'drbfm.mainline.statusUnavailable',
};

export default function DRBFMMainlineWorkflow({ sessionId, tenantId, status, onSession }: {
  sessionId?: string | null; tenantId: string; status: MainlineStatus; onSession: (id: string) => void;
}) {
  const enabled = status === 'enabled';
  const notice = STATUS_NOTICE[status];
  const { t } = useTranslation();
  const [state, setState] = useState<MainlineState | null>(null);
  const [inputs, setInputs] = useState<MainlineInputs>(emptyInputs);
  const [inputsDirty, setInputsDirty] = useState(false);
  const [itemName, setItemName] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState('');
  const [hasPending, setHasPending] = useState(false);
  const [exportScope, setExportScope] = useState('all');
  const pendingKey = `drbfm-mainline-pending:${tenantId}:${sessionId ?? 'new'}`;
  const sessionRef = useRef(sessionId);
  sessionRef.current = sessionId;
  const savedInputs = state && [...state.artifacts].reverse().find(a => a.status === 'active' && a.payload.mainline_inputs);
  const hydrate = (next: MainlineState, loadInputs = false) => {
    setState(next); setItemName(next.session.item_name);
    if (loadInputs) {
      setInputsDirty(false);
      const saved = [...next.artifacts].reverse().find(a => a.status === 'active' && a.payload.mainline_inputs);
      if (saved) {
        const normalized = { ...emptyInputs };
        Object.keys(emptyInputs).forEach(key => { if (key in saved.payload) Object.assign(normalized, { [key]: saved.payload[key as keyof typeof saved.payload] }); });
        (['old_document_id', 'new_document_id', 'report_document_id'] as const).forEach(key => { normalized[key] = saved.payload[key]; });
        // Older API sessions omitted the setting. Restore the server-resolved
        // legacy mode instead of silently changing their next run to focused.
        normalized.extraction_mode = saved.payload.extraction_mode ?? next.extraction_mode ?? null;
        setInputs(normalized);
      }
    }
  };
  useEffect(() => {
    let cancelled = false;
    setState(null); setError(''); setInputs(emptyInputs); setInputsDirty(false);
    setHasPending(Boolean(sessionStorage.getItem(pendingKey)));
    if (sessionId) {
      setBusy(true);
      mainlineApi.state(sessionId).then(next => { if (!cancelled) hydrate(next, true); })
        .catch(() => { if (!cancelled) setError(t('drbfm.mainline.loadFailed')); })
        .finally(() => { if (!cancelled) setBusy(false); });
    }
    return () => { cancelled = true; };
  }, [sessionId, tenantId, pendingKey, t]);

  const errorMessage = (err: unknown) => mainlineErrorMessage(t, err);
  const execute = async (command: MainlineCommand): Promise<boolean> => {
    if (!sessionId || busyRef.current) return false;
    busyRef.current = true; setBusy(true); setError('');
    sessionStorage.setItem(pendingKey, JSON.stringify(command)); setHasPending(true);
    try {
      const response = await mainlineApi.command(sessionId, command);
      if (sessionRef.current !== sessionId) return false;
      hydrate(response.workflow, command.action === 'save_inputs');
      sessionStorage.removeItem(pendingKey); setHasPending(false);
      return true;
    } catch (err) {
      setError(errorMessage(err));
      const code = (err as { code?: string }).code;
      if (code === 'HTTP_409' || code === 'HTTP_422') {
        sessionStorage.removeItem(pendingKey); setHasPending(false);
      } else if (code === 'HTTP_502') {
        sessionStorage.setItem(pendingKey, JSON.stringify({ ...command, request_id: crypto.randomUUID() }));
      }
      return false;
    }
    finally { busyRef.current = false; setBusy(false); }
  };
  const send = async (body: Omit<MainlineCommand, 'request_id' | 'expected_version'>): Promise<boolean> => {
    if (!state || !enabled) return false;
    const cached = sessionStorage.getItem(pendingKey);
    const prior = cached ? JSON.parse(cached) as MainlineCommand : null;
    const { request_id: _id, expected_version: _v, ...priorBody } = prior ?? {};
    return execute(prior && JSON.stringify(priorBody) === JSON.stringify(body) ? prior
      : { ...body, request_id: crypto.randomUUID(), expected_version: state.version });
  };
  const saveInputs = async () => {
    if (state) { await send({ action: 'save_inputs', inputs }); return; }
    if (busyRef.current || !itemName.trim() || !enabled) return;
    busyRef.current = true; setBusy(true); setError('');
    const body = JSON.stringify({ itemName, inputs });
    const previous = sessionStorage.getItem(pendingKey);
    const prior = previous ? JSON.parse(previous) as { body?: string; id?: string } : {};
    const id = prior.body === body && prior.id ? prior.id : crypto.randomUUID();
    sessionStorage.setItem(pendingKey, JSON.stringify({ body, id }));
    try {
      const next = await mainlineApi.start(id, itemName.trim(), inputs);
      sessionStorage.removeItem(pendingKey); hydrate(next, true); onSession(next.session.id);
    } catch (err) { setError(errorMessage(err)); }
    finally { busyRef.current = false; setBusy(false); }
  };
  const reload = async () => {
    if (!sessionId) return;
    setBusy(true);
    try { hydrate(await mainlineApi.state(sessionId), !inputsDirty); sessionStorage.removeItem(pendingKey); setHasPending(false); setError(''); }
    catch { setError(t('drbfm.mainline.loadFailed')); }
    finally { setBusy(false); }
  };
  const download = async (mode: string) => {
    if (!state || busyRef.current) return;
    setBusy(true); busyRef.current = true;
    try {
      const blob = await api.get<Blob>(`/drbfm/sessions/${state.session.id}/export/excel`, {
        params: { mode, scope: exportScope }, responseType: 'blob',
      });
      const url = URL.createObjectURL(blob); const link = document.createElement('a');
      link.href = url; link.download = `drbfm_${state.session.id}_${exportScope}_${mode}.xlsx`;
      link.click(); URL.revokeObjectURL(url);
    } catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); busyRef.current = false; }
  };
  return <div className="grid gap-6">
    <p className="text-sm text-muted-foreground">{t('drbfm.mainline.progress')}</p>
    {error && <Alert variant="destructive" role="alert">{error}</Alert>}
    {/* Polite while checking; keyed so each outcome mounts as a new alert instead of re-labelling the node. */}
    {notice && <Alert key={status} {...(status === 'checking' ? { role: 'status' } : {})}>{t(notice)}</Alert>}
    {busy && <p role="status">{t(inputs.pattern === 'old_new_documents' && inputs.extraction_mode === 'exhaustive'
      ? 'drbfm.mainline.exhaustiveWorking' : 'drbfm.mainline.working')}</p>}
    {state && <div className="flex flex-wrap gap-2"><Button variant="outline" disabled={busy} onClick={reload}>{t('drbfm.mainline.reload')}</Button>
      {hasPending && <Button variant="outline" disabled={busy || !enabled} onClick={() => {
        const pending = sessionStorage.getItem(pendingKey); if (pending) execute(JSON.parse(pending));
      }}>{t('drbfm.mainline.retry')}</Button>}</div>}
    {!state && <label>{t('drbfm.mainline.target')}<Input value={itemName} onChange={e => setItemName(e.target.value)} disabled={busy || !enabled} /></label>}
    <InputsPanel value={inputs} onChange={next => { setInputs(next); setInputsDirty(true); }} disabled={busy || !enabled} onSave={saveInputs} scope={savedInputs?.payload.reference_scope} />
    {state && inputsDirty && <Alert>{t('drbfm.mainline.saveInputsFirst')}</Alert>}
    {state && <>
      {inputs.pattern === 'change_report' && <Button variant="outline" disabled={busy || !enabled || inputsDirty} onClick={() => send({ action: 'import_preview' })}>{t('drbfm.mainline.importPreview')}</Button>}
      {state.artifacts.filter(a => a.status === 'active' && a.payload.mainline_proposal).map(a =>
        <ProposalPanel key={a.id} artifact={a} state={state} disabled={busy || !enabled || inputsDirty} send={send} />)}
      {(['design_change', 'failure_mode', 'countermeasure'] as RowKind[]).map(kind =>
        <RowsPanel key={kind} kind={kind} state={state} disabled={busy || !enabled || inputsDirty} send={send} />)}
      <section className="grid gap-3 rounded border p-4"><h2 className="text-lg font-semibold">5. {t('drbfm.mainline.export')}</h2>
        <Alert variant={state.readiness.readiness === 'draft' ? 'warning' : 'success'}>
          <span>{t(`drbfm.mainline.${state.readiness.readiness}`)} · {t('drbfm.mainline.blockers', { count: state.readiness.blockers.length })}</span>
        </Alert>
        <ul className="text-sm">{state.readiness.blockers.map((b, i) => {
          const row = [...state.design_changes, ...state.failure_modes, ...state.countermeasures].find(r => r.id === b.artifact_id);
          return <li key={`${b.artifact_id}-${i}`}>{t(`drbfm.mainline.blocker_${b.type}`, { defaultValue: t('drbfm.mainline.unconfirmed') })}{row ? ` · ${rowTitle(row).slice(0, 100)}` : ''}</li>;
        })}</ul>
        <label>{t('drbfm.mainline.exportScope')}<FieldSelect className="inline-flex w-auto min-w-32 ml-2" value={exportScope} onValueChange={setExportScope}
          options={[{ value: 'all', label: t('drbfm.mainline.all') }, { value: 'accepted', label: t('drbfm.mainline.acceptedScope') }]} /></label>
        <p className="text-sm">{t('drbfm.mainline.exportHint')}</p>
        <div className="flex flex-wrap gap-2">{['draft', 'reviewed', 'scored'].map(mode =>
          <Button key={mode} variant="outline" disabled={busy || inputsDirty || !state.readiness.allowed_export_modes.includes(mode)} onClick={() => download(mode)}>{t(`drbfm.mainline.export_${mode}`)}</Button>)}</div>
      </section>
      <PanelDisclosure title={<>{t('drbfm.mainline.history')} ({state.review_events.length})</>}>
        {state.review_events.map(event => <p className="my-2 text-sm" key={event.id}>{event.created_at} · {event.created_by} · {event.from_status} → {event.to_status} · {event.reviewer_note}</p>)}
      </PanelDisclosure>
    </>}
  </div>;
}
